import { VL_MARKETPLACE_OPTIONS } from './api/vlListing.api';

export const VL_ENABLED_MARKETPLACES_CACHE_KEY = 'qa_vl_enabled_marketplaces_by_tp';
export const VL_SHARED_STATUS_CACHE_KEY = 'qa_vl_shared_status_by_tp';

const ALL_IDS = VL_MARKETPLACE_OPTIONS.map((m) => m.id);

function readCache(): Record<string, string[]> {
  try {
    const raw = sessionStorage.getItem(VL_ENABLED_MARKETPLACES_CACHE_KEY);
    if (!raw) return {};
    const parsed = JSON.parse(raw) as Record<string, string[]>;
    return parsed && typeof parsed === 'object' ? parsed : {};
  } catch {
    return {};
  }
}

function writeCache(next: Record<string, string[]>): void {
  try {
    sessionStorage.setItem(VL_ENABLED_MARKETPLACES_CACHE_KEY, JSON.stringify(next));
  } catch {
    // ignore quota / private mode
  }
}

/** Normalize marketplace id list (dedupe, known ids only, preserve order). */
export function normalizeEnabledMarketplaceIds(ids: string[] | null | undefined): string[] {
  if (!ids?.length) return [...ALL_IDS];
  const known = new Set(ALL_IDS.map((id) => id.toLowerCase()));
  const seen = new Set<string>();
  const out: string[] = [];
  for (const raw of ids) {
    const id = String(raw || '').trim().toLowerCase();
    if (!id || !known.has(id) || seen.has(id)) continue;
    seen.add(id);
    const match = ALL_IDS.find((m) => m.toLowerCase() === id);
    if (match) out.push(match);
  }
  return out.length ? out : [...ALL_IDS];
}

export function getCachedEnabledMarketplaceIds(tpId: string): string[] | null {
  const key = String(tpId || '').trim();
  if (!key) return null;
  const cached = readCache()[key];
  if (!Array.isArray(cached) || !cached.length) return null;
  return normalizeEnabledMarketplaceIds(cached);
}

export function getEnabledMarketplaceIdsForTicket(tpId: string): string[] {
  return getCachedEnabledMarketplaceIds(tpId) ?? [...ALL_IDS];
}

export function getVlMarketplaceIdsForTicket(
  tpId: string,
  ticket?: Record<string, any> | null,
): string[] {
  if (ticket && Array.isArray(ticket.tp_vl_marketplaces)) {
    const storedValues = new Set(
      ticket.tp_vl_marketplaces
        .map((value: unknown) => String(value ?? '').trim().toLowerCase())
        .filter(Boolean),
    );
    return VL_MARKETPLACE_OPTIONS.filter(
      (marketplace) =>
        storedValues.has(marketplace.id.toLowerCase()) ||
        storedValues.has(marketplace.label.toLowerCase()),
    ).map((marketplace) => marketplace.id);
  }
  return getEnabledMarketplaceIdsForTicket(tpId);
}

export function getVlMarketplaceLabels(ids: string[] | null | undefined): string[] {
  if (!Array.isArray(ids)) return [];
  const selected = new Set(ids.map((id) => String(id || '').trim().toLowerCase()));
  return VL_MARKETPLACE_OPTIONS.filter((marketplace) =>
    selected.has(marketplace.id.toLowerCase()),
  ).map((marketplace) => marketplace.label);
}

export function setCachedEnabledMarketplaceIds(
  tpId: string,
  ids: string[],
): void {
  const key = String(tpId || '').trim();
  if (!key) return;
  const cache = readCache();
  cache[key] = normalizeEnabledMarketplaceIds(ids);
  writeCache(cache);
}

export function setCachedEnabledMarketplaceIdsForMany(
  tpIds: string[],
  ids: string[],
): void {
  const normalized = normalizeEnabledMarketplaceIds(ids);
  const cache = readCache();
  for (const tpId of tpIds) {
    const key = String(tpId || '').trim();
    if (key) cache[key] = normalized;
  }
  writeCache(cache);
}

function readSharedCache(): Record<string, boolean> {
  try {
    const raw = sessionStorage.getItem(VL_SHARED_STATUS_CACHE_KEY);
    if (!raw) return {};
    const parsed = JSON.parse(raw) as Record<string, boolean>;
    return parsed && typeof parsed === 'object' ? parsed : {};
  } catch {
    return {};
  }
}

function writeSharedCache(next: Record<string, boolean>): void {
  try {
    sessionStorage.setItem(VL_SHARED_STATUS_CACHE_KEY, JSON.stringify(next));
  } catch {
    // ignore quota / private mode
  }
}

export function getCachedVlSharedStatus(tpId: string): boolean | null {
  const key = String(tpId || '').trim();
  if (!key) return null;
  const cached = readSharedCache()[key];
  return typeof cached === 'boolean' ? cached : null;
}

/** Prefer the persisted VL Share value; missing legacy values default to false. */
export function getVlSharedStatusForTicket(
  tpId: string,
  ticket?: Record<string, any> | null,
): boolean {
  if (ticket && typeof ticket.tp_vl_share === 'boolean') {
    return ticket.tp_vl_share;
  }
  const cached = getCachedVlSharedStatus(tpId);
  return cached ?? false;
}

export function setCachedVlSharedStatus(tpId: string, shared: boolean): void {
  const key = String(tpId || '').trim();
  if (!key) return;
  const cache = readSharedCache();
  cache[key] = shared;
  writeSharedCache(cache);
}

export function setCachedVlSharedStatusForMany(tpIds: string[], shared: boolean): void {
  const cache = readSharedCache();
  for (const tpId of tpIds) {
    const key = String(tpId || '').trim();
    if (key) cache[key] = shared;
  }
  writeSharedCache(cache);
}

export function ticketHasVlListingMarkers(
  ticket: Record<string, any> | null | undefined,
): boolean {
  if (!ticket) return false;
  const listing = [
    ticket.tp_vl_listing_id,
    ticket.vlListingId,
    ticket.tpVlListingId,
    ticket.listing_id,
    ticket.listingId,
    ticket.tp_listing_id,
  ].map((value) => String(value ?? '').trim()).find(Boolean) || '';
  const item = [
    ticket.tp_vl_item_id,
    ticket.vlItemId,
    ticket.tpVlItemId,
    ticket.item_id,
    ticket.itemId,
    ticket.tp_item_id,
  ].map((value) => String(value ?? '').trim()).find(Boolean) || '';
  return Boolean(listing || item);
}

export function isVlListedTicketStatus(status: string | null | undefined): boolean {
  const key = String(status || '')
    .toLowerCase()
    .replace(/[^a-z]/g, '');
  return key === 'list' || key === 'listed';
}

/** Short label for compact marketplace chips in Listed rows. */
export function marketplaceShortLabel(id: string): string {
  const match = VL_MARKETPLACE_OPTIONS.find((m) => m.id === id);
  if (!match) return id.slice(0, 2).toUpperCase();
  if (id === 'stubhub') return 'SH';
  if (id === 'seatgeek') return 'SG';
  if (id === 'vivid') return 'VS';
  if (id === 'ticketnetwork') return 'TN';
  if (id === 'ticketevolution') return 'TE';
  if (id === 'tickpick') return 'TP';
  if (id === 'gametime') return 'GT';
  if (id === 'ticketsnow') return 'TN';
  if (id === 'fanx') return 'FX';
  if (id === 'axs') return 'AX';
  if (id === 'gotickets') return 'GO';
  return match.label.slice(0, 2).toUpperCase();
}

/** Shown on table/modal while Listed → Delisted VL cleanup runs. */
export const LISTED_DELIST_BUSY_MESSAGE =
  'Appending "." to the Victory Live row, unsharing, then deleting inventory…';

export function formatUnshareDelistSuccessMessage(ticketCount: number): string {
  if (ticketCount > 1) {
    return `${ticketCount} tickets updated with a row period, unshared, and removed from Victory Live, then moved to Delisted.`;
  }
  return 'Ticket updated with a row period, unshared, and removed from Victory Live, then moved to Delisted.';
}

export function formatDelistCleanupFailureMessage(params: {
  failedCount: number;
  failedDetails?: Array<{ failedStep?: number | null }>;
  fallbackMessage?: string;
}): string {
  const { failedCount, failedDetails, fallbackMessage } = params;
  const steps = (failedDetails || []).map((detail) => detail.failedStep);
  const unshareOrDeleteFailed = steps.some((step) => step === 2 || step === 3);
  const periodFailed = steps.some((step) => step === 1);
  if (unshareOrDeleteFailed && !periodFailed) {
    return failedCount > 1
      ? `${failedCount} tickets had the row period applied on Victory Live, but unshare or delete failed. Those seats were left Listed — retry to finish cleanup.`
      : 'Ticket had the row period applied on Victory Live, but unshare or delete failed. It was left Listed — retry to finish cleanup.';
  }
  if (failedCount > 1) {
    return `${failedCount} ticket(s) could not be delisted on Victory Live — those seats were left Listed.`;
  }
  return fallbackMessage || 'Delist failed on Victory Live — admin status was not changed. Please try again.';
}
